// Renders a single parent question for the placement wizard (HOP-23).

import { useState, useCallback } from 'react'
import type { EvalQuestionV2 } from '@/types'
import '@/styles/placement-flow.css'

interface EvaluationQuestionV2Props {
  question: EvalQuestionV2
  onAnswer: (questionId: string, answer: string | string[]) => void
  submitting?: boolean
}

function OptionRow({
  label,
  selected,
  multi,
  onClick,
}: {
  label: string
  selected: boolean
  multi: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        'pq-option',
        'pq-option--compact',
        multi ? 'pq-option--multi' : '',
        selected ? 'is-selected' : '',
      ].filter(Boolean).join(' ')}
    >
      <span className="pq-option__check">
        {selected && (
          <svg
            width="14"
            height="14"
            viewBox="0 0 20 20"
            fill="none"
            stroke="var(--yellow)"
            strokeWidth="3"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M4 11l4 4 8-9" />
          </svg>
        )}
      </span>
      <span className="pq-option__label pq-option__label--compact">{label}</span>
    </button>
  )
}

export function EvaluationQuestionV2({
  question,
  onAnswer,
  submitting = false,
}: EvaluationQuestionV2Props) {
  const [selectedLabel, setSelectedLabel] = useState<string | null>(null)
  const [selectedLabels, setSelectedLabels] = useState<string[]>([])

  const isMulti = question.type === 'multi'
  const ready = isMulti ? selectedLabels.length > 0 : selectedLabel !== null

  const toggle = useCallback(
    (label: string) => {
      if (submitting) return
      if (isMulti) {
        setSelectedLabels((prev) =>
          prev.includes(label) ? prev.filter((o) => o !== label) : [...prev, label],
        )
      } else {
        setSelectedLabel(label)
      }
    },
    [isMulti, submitting],
  )

  const handleSubmit = useCallback(() => {
    if (!ready || submitting) return
    if (isMulti) onAnswer(question.id, selectedLabels)
    else onAnswer(question.id, selectedLabel!)
  }, [ready, submitting, isMulti, onAnswer, question.id, selectedLabels, selectedLabel])

  const isSelected = (label: string) =>
    isMulti ? selectedLabels.includes(label) : selectedLabel === label

  return (
    <div className="evaluation-question">
      <h1
        className={[
          'pq-title',
          'evaluation-question__title',
          isMulti ? 'evaluation-question__title--multi' : '',
        ].filter(Boolean).join(' ')}
      >
        {question.question}
      </h1>
      {isMulti && (
        <p className="evaluation-question__hint">
          Pick as many as you like.
        </p>
      )}

      <div className="evaluation-question__options">
        {question.options.map((option) => (
          <OptionRow
            key={option.value || option.label}
            label={option.label}
            multi={isMulti}
            selected={isSelected(option.label)}
            onClick={() => toggle(option.label)}
          />
        ))}
      </div>

      <div className="pq-cta-row evaluation-question__cta-row">
        <button
          type="button"
          disabled={!ready || submitting}
          onClick={handleSubmit}
          className="hs-press pq-cta-btn evaluation-question__cta"
        >
          {isMulti ? 'Done' : 'Next question'}
          <svg
            width="19"
            height="19"
            viewBox="0 0 20 20"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.4"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3 10h13M11 4l6 6-6 6" />
          </svg>
        </button>
      </div>
    </div>
  )
}
